// @flow

import React from "react";
import { API, PubSub } from "aws-amplify";
import {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useContext,
  useCallback,
} from "react";

import { Container, Row, Col, Table, Stack } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";

import UserContext from "./../utils/UserContext";
import { getSessionUsernameTopic } from "../utils/SessionUsers";

import {
  queryScriptSheetsBySessionSheetsIdIndex,
  queryUserKeyCommandsByUserIdCommandNameIndex as queryUserKeyCommands,
  queryBookmarksBySessionIdRowIndex,
} from "./../graphql/queries";

import {
  updateSession as updateSessionMutation,
  updateScriptSheets as updateScriptSheetsMutation,
  createScriptSheets as createScriptSheetsMutation,
  deleteScriptSheets as deleteScriptSheetsMutation,
  createBookmarks as createBookmarksMutation,
} from "./../graphql/mutations";

import { onUpdateSession } from "../graphql/subscriptions";

import { sendMessage } from "./../utils/MessageSender";
import SheetsLoadView from "./SheetsLoadView.react";
import BookmarksBar from "./BookmarksBar.react";
import * as Sentry from "@sentry/react";
import { useVirtualizer } from "@tanstack/react-virtual";

type Props = $ReadOnly<{
  sessionId: string,
  sessionName: string,
  setSheetsInput: React.Dispatch<React.SetStateAction<any>>,
  setSheetsInputIndex: React.Dispatch<React.SetStateAction<any>>,
  isYoutubeView: Boolean,
  prevDisplay: Boolean,
  selectedUser: string,
}>;

async function deleteScriptSheets(id: string) {
  const ret = await API.graphql({
    query: deleteScriptSheetsMutation,
    variables: {
      input: {
        id: id,
      },
    },
  });
}

async function updateSessionSheetsUpdateCount(sessionId) {
  const ret = await API.graphql({
    query: updateSessionMutation,
    variables: {
      input: {
        id: sessionId,
        sheetsUpdateCount: 1,
      },
    },
  });
}

// unmount flush 경로에서 호출되면 실패를 UI로 알릴 방법이 없으므로 Sentry로 남긴다
async function updateScriptSheetsScript(id: string, script: string) {
  try {
    await API.graphql({
      query: updateScriptSheetsMutation,
      variables: {
        input: {
          id: id,
          script: script,
        },
      },
    });
  } catch (e) {
    Sentry.captureException(e, {
      extra: { context: "updateScriptSheetsScript", id },
    });
  }
}

async function updateScriptSheetsTranslation(
  id: string,
  translation: string
) {
  try {
    await API.graphql({
      query: updateScriptSheetsMutation,
      variables: {
        input: {
          id: id,
          translation: translation,
        },
      },
    });
  } catch (e) {
    Sentry.captureException(e, {
      extra: { context: "updateScriptSheetsTranslation", id },
    });
  }
}

async function updateScriptSheetsBackgroundColor(
  id: string,
  backgroundColor: string
) {
  const ret = await API.graphql({
    query: updateScriptSheetsMutation,
    variables: {
      input: {
        id: id,
        backgroundColor: backgroundColor,
      },
    },
  });
}

async function updateScriptSheetsTextColor(id: string, textColor: string) {
  const ret = await API.graphql({
    query: updateScriptSheetsMutation,
    variables: {
      input: {
        id: id,
        textColor: textColor,
      },
    },
  });
}

async function fetchUserKeyCommands(userId) {
  try {
    const apiData = await API.graphql({
      query: queryUserKeyCommands,
      variables: {
        userId: userId,
      },
    });
    return apiData.data.queryUserKeyCommandsByUserIdCommandNameIndex.items;
  } catch (e) {
    console.log(e);
  }
}

// 가상화된 행은 viewport 밖으로 스크롤되면 unmount된다. 포커스된 노드가 DOM에서
// 제거될 때 브라우저는 blur를 발생시키지 않아 onBlur 저장 경로가 통째로 누락되므로,
// 타이핑된 값을 ref에 담아 두고 unmount 시 flush해서 편집분 유실을 막는다.
const EditableCell = ({
  textareaId,
  lineId,
  position,
  initialValue,
  saveValue,
  sessionId,
  setEditable,
}) => {
  const draftRef = useRef(null);
  const flushRef = useRef(null);

  // 편집 중 다른 셀을 더블클릭하면 blur/unmount가 새 셀의 편집 상태보다 늦게
  // 처리될 수 있다. 무조건 0으로 덮으면 방금 연 편집기가 닫히므로 위치를 확인한다.
  function closeEditor() {
    setEditable((prev) => (prev === position ? 0 : prev));
  }

  function commit(value) {
    // 정상 저장 경로와 unmount flush가 이중 저장되지 않도록 draft를 먼저 비운다
    draftRef.current = null;
    saveValue(lineId, value);
    // 스크롤로 돌아왔을 때 stale 값으로 편집기가 다시 열리지 않도록 편집 상태를 닫는다
    closeEditor();
    setTimeout(function () {
      updateSessionSheetsUpdateCount(sessionId);
    }, 300);
  }

  // cleanup은 mount 시점 클로저를 잡으므로 최신 동작을 ref로 건네준다
  flushRef.current = () => {
    if (draftRef.current !== null) {
      commit(draftRef.current);
    } else {
      // 타이핑이 없었어도 편집기는 닫는다. 열어둔 채로 두면 행이 다시 그려질 때
      // autoFocus가 스크롤 위치를 되돌려 사용자의 스크롤과 싸운다.
      closeEditor();
    }
  };

  useEffect(() => {
    return () => flushRef.current();
  }, []);

  return (
    <textarea
      autoFocus
      id={textareaId}
      style={{
        width: "100%",
        height: "100%",
        fontWeight: "normal",
      }}
      defaultValue={initialValue}
      onChange={(event) => {
        draftRef.current = event.target.value;
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          commit(event.target.value);
        } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.stopPropagation();
        } else if (event.key === "Escape") {
          // 취소 동작이므로 flush하지 않는다
          draftRef.current = null;
          closeEditor();
        }
      }}
      onFocus={(e) => {
        const value = e.target.value;
        e.target.value = "";
        e.target.value = value;
      }}
      onBlur={(event) => {
        commit(event.target.value);
      }}
    ></textarea>
  );
};

// 선택 행 표시 색. 테두리와 이름표가 같은 색을 쓴다.
const MY_SELECTION_COLOR = "#337BB5";
const PARTNER_SELECTION_COLOR = "#009405";

// 선택 행 이름표(내 것 `me` / 파트너 아이디).
//
// 파트너를 보고 있을 때만 뜬다. 혼자 작업할 때는 헷갈릴 상대가 없어 이름표가 노이즈다.
//
// 시트는 @tanstack/react-virtual의 measureElement로 <tr> 실제 높이를 재기 때문에,
// 이름표가 레이아웃 흐름에 들어가면 그 행만 높이가 커져 측정값이 흔들리고 스크롤이 튄다.
// 그래서 셀 안의 relative 컨테이너를 기준으로 absolute로 띄워 흐름에서 완전히 뺀다.
// (<tr>에 position: relative를 주는 방식은 브라우저마다 동작이 달라 쓰지 않는다.)
//
// labels는 배열이므로 나중에 파트너가 N명으로 늘어도 그대로 쓸 수 있다.
const MAX_LABEL_LENGTH = 10;

// 이름표는 옆 행 위에 떠 있어서 넓어질수록 그 행을 더 가린다. 아이디가 아무리 길어도
// 앞 10자까지만 남기고 잘라 너비 상한을 글자 수로 못박는다.
function truncateLabel(text) {
  return text.length > MAX_LABEL_LENGTH
    ? `${text.slice(0, MAX_LABEL_LENGTH)}…`
    : text;
}

const SelectionLabels = ({ labels, below }) => (
  <div
    style={{
      position: "absolute",
      left: "-2px",
      // 행 바깥(위)에 붙여 이 행의 script·translation 텍스트를 가리지 않게 한다.
      // 단 첫 행은 위쪽이 thead에 가려지므로 아래로 내린다.
      ...(below ? { top: "100%" } : { bottom: "100%" }),
      display: "flex",
      gap: "2px",
      // 이름표는 옆 행 위에 떠 있다. 그 자리를 클릭하면 이름표가 아니라
      // 아래에 깔린 행이 선택되어야 한다.
      pointerEvents: "none",
      zIndex: 3,
    }}
  >
    {labels.map((label) => (
      <span
        key={label.key}
        style={{
          fontFamily: "Arial",
          fontSize: "10px",
          fontWeight: "bold",
          lineHeight: "14px",
          padding: "0px 4px",
          borderRadius: below ? "0px 0px 3px 3px" : "3px 3px 0px 0px",
          background: label.color,
          color: "#FFFFFF",
          // 자르기는 truncateLabel()이 하고, 여기는 한글처럼 폭이 넓은 글자로
          // 10자를 채웠을 때를 대비한 안전장치다.
          maxWidth: "120px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {truncateLabel(label.text)}
      </span>
    ))}
  </div>
);

const SheetRow = React.memo(
  React.forwardRef(({
    line,
    idx,
    isSelected,
    setSelectedRow,
    isEditableScript,
    editableScript,
    setEditableScript,
    isEditableTranslation,
    editableTranslation,
    setEditableTranslation,
    isYoutubeView,
    prevDisplay,
    sessionId,
    handleSheetsInput,
    setSheetsInput,
    setSheetsInputIndex,
    setShouldScroll,
    isPartnerSelected,
    partnerName,
    showName,
    dataIndex,
  }, ref) => {
    // 같은 행에 둘 다 걸리면 나란히 보여준다. 테두리는 한 색만 나오므로
    // (handleStyle이 selected를 먼저 검사) 이름표가 겹침을 알려주는 유일한 단서다.
    const selectionLabels = [];
    if (partnerName) {
      if (isSelected) {
        selectionLabels.push({
          key: "me",
          text: "me",
          color: MY_SELECTION_COLOR,
        });
      }
      if (isPartnerSelected) {
        selectionLabels.push({
          key: `partner:${partnerName}`,
          text: partnerName,
          color: PARTNER_SELECTION_COLOR,
        });
      }
    }
    const hasSelectionLabels = selectionLabels.length > 0;

    const styles = {
      td: {
        fontFamily: "Arial",
        padding: "5px",
        verticalAlign: "middle",
        wordBreak: "break-word",
        whiteSpace: "pre-wrap",
      },
      tdIndex: {
        fontFamily: "Arial",
        padding: "5px",
        verticalAlign: "middle",
      },
      // 이름표의 기준이 되는 셀. showName에 따라 열 수가 바뀌어도 번호 칸은
      // 항상 첫 번째라 위치가 흔들리지 않는다.
      tdRowNumber: {
        fontFamily: "Arial",
        padding: "5px",
        verticalAlign: "middle",
        position: "relative",
      },
      tdName: {
        fontFamily: "Arial",
        padding: "0px",
        verticalAlign: "middle",
        whiteSpace: "pre-wrap",
      },
    };

    function handleClick(event, idx, type) {
      if (event.detail === 1) {
        if (idx + 1 !== editableScript && idx + 1 !== editableTranslation) {
          setShouldScroll(false);
          setSelectedRow(idx);
          document.getElementById("chat-input-field").focus();
        }
      } else if (event.detail === 2 && type === "script") {
        setEditableScript(idx + 1);
      } else if (event.detail === 2 && type === "translation") {
        setEditableTranslation(idx + 1);
      }
    }

    function handleSendTranslation(translation, sessionId, index) {
      sendMessage({
        sessionId,
        text: translation,
        scriptIndex: index,
        context: "SheetsView.handleSendTranslation",
      });
    }

    function handleStyle(selected, partnerSelected, editable) {
      if (selected) {
        return {
          border: `2px solid ${MY_SELECTION_COLOR}`,
          background: line.backgroundColor,
          color: line.textColor,
          fontWeight: "bold",
          cursor: "default",
        };
      } else if (selected && editable) {
        return {
          background: line.backgroundColor,
          color: line.textColor,
          cursor: "default",
        };
      } else if (partnerSelected) {
        return {
          border: `2px solid ${PARTNER_SELECTION_COLOR}`,
          background: line.backgroundColor,
          color: line.textColor,
          fontWeight: "bold",
          cursor: "default",
        };
      } else {
        return {
          background: line.backgroundColor,
          color: line.textColor,
          cursor: "default",
        };
      }
    }

    return (
      <tr
        style={handleStyle(isSelected, isPartnerSelected)}
        ref={ref}
        data-index={dataIndex}
      >
        <td
          align="center"
          style={styles.tdRowNumber}
          onClick={(event) => {
            if (event.detail === 2) {
              event.stopPropagation();
            }
            handleClick(event, idx);
          }}
        >
          {hasSelectionLabels ? (
            <SelectionLabels labels={selectionLabels} below={idx === 0} />
          ) : null}
          {idx + 1}
          {/* {line.index} */}
        </td>
        {showName ? (
          <td
            align="center"
            style={styles.tdName}
            onClick={(event) => {
              if (event.detail === 2) {
                event.stopPropagation();
              }
              handleClick(event, idx);
            }}
          >
            {line.name}
          </td>
        ) : null}
        <td
          style={styles.td}
          onClick={(event) => {
            handleClick(event, idx, "script");
          }}
        >
          {isEditableScript ? (
            <EditableCell
              textareaId={`textarea${idx}Script`}
              lineId={line.id}
              position={idx + 1}
              initialValue={line.script}
              saveValue={updateScriptSheetsScript}
              sessionId={sessionId}
              setEditable={setEditableScript}
            />
          ) : (
            line.script
          )}
        </td>
        <td
          style={styles.td}
          onClick={(event) => {
            handleClick(event, idx, "translation");
          }}
        >
          {isEditableTranslation ? (
            <EditableCell
              textareaId={`textarea${idx}Translation`}
              lineId={line.id}
              position={idx + 1}
              initialValue={line.translation}
              saveValue={updateScriptSheetsTranslation}
              sessionId={sessionId}
              setEditable={setEditableTranslation}
            />
          ) : (
            line.translation
          )}
        </td>
        {/* <td align="center">{line.wordCount}</td> */}
        <td style={styles.tdIndex}>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={isYoutubeView && prevDisplay}
            onClick={(event) => {
              document.getElementById("chat-input-field").focus();
              handleSendTranslation(line.translation, sessionId, idx + 1);
              handleClick(event, idx + 1);
            }}
          >
            Send
          </Button>
        </td>
      </tr>
    );
  })
);

const SheetRows = ({
  sheets,
  setSheets,
  selectedRow,
  setSelectedRow,
  editableScript,
  setEditableScript,
  editableTranslation,
  setEditableTranslation,
  isYoutubeView,
  prevDisplay,
  sessionId,
  setSheetsInput,
  setSheetsInputIndex,
  handleSheetsInput,
  partnerSelectedRow,
  partnerName,
  showName,
  setShouldScroll,
  rowVirtualizer,
  tbodyRef,
}) => {
  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  // measurement의 start/end는 scrollMargin(= thead 등 tbody 위쪽 높이)을 포함한
  // 스크롤 컨테이너 좌표계다. spacer는 tbody 안쪽 좌표계이므로 다시 빼준다.
  const scrollMargin = rowVirtualizer.options.scrollMargin;
  const paddingTop =
    virtualItems.length > 0 ? virtualItems[0].start - scrollMargin : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? totalSize - (virtualItems[virtualItems.length - 1].end - scrollMargin)
      : 0;
  const colCount = showName ? 5 : 4;

  return (
    <tbody ref={tbodyRef}>
      {paddingTop > 0 && (
        <tr aria-hidden>
          <td
            colSpan={colCount}
            style={{ height: paddingTop, padding: 0, border: 0 }}
          />
        </tr>
      )}
      {sheets &&
        virtualItems.map((virtualRow) => {
          const idx = virtualRow.index;
          const line = sheets[idx];
          if (!line) return null;
          return (
            <SheetRow
              key={virtualRow.key}
              line={line}
              idx={idx}
              dataIndex={idx}
              ref={rowVirtualizer.measureElement}
              isSelected={idx === selectedRow}
              setSelectedRow={setSelectedRow}
              editableScript={editableScript}
              editableTranslation={editableTranslation}
              setEditableScript={setEditableScript}
              isEditableScript={idx + 1 === editableScript}
              setEditableTranslation={setEditableTranslation}
              isEditableTranslation={idx + 1 === editableTranslation}
              isYoutubeView={isYoutubeView}
              prevDisplay={prevDisplay}
              sessionId={sessionId}
              setSheetsInput={setSheetsInput}
              setSheetsInputIndex={setSheetsInputIndex}
              handleSheetsInput={handleSheetsInput}
              isPartnerSelected={idx === partnerSelectedRow}
              partnerName={partnerName}
              showName={showName}
              setShouldScroll={setShouldScroll}
            />
          );
        })}
      {paddingBottom > 0 && (
        <tr aria-hidden>
          <td
            colSpan={colCount}
            style={{ height: paddingBottom, padding: 0, border: 0 }}
          />
        </tr>
      )}
    </tbody>
  );
};

export default function SheetsView(props: Props): React$Element<any> {
  const userContext = useContext(UserContext);

  const [sheets, setSheets] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedRow, setSelectedRow] = useState(0);
  const [partnerSelectedRow, setPartnerSelectedRow] = useState(-1);
  const [subscription, setSubscription] = useState();
  // const [editableRow, setEditableRow] = useState(0);
  const [editableScript, setEditableScript] = useState(0);
  const [editableTranslation, setEditableTranslation] = useState(0);
  const [editing, setEditing] = useState("");
  const [isInsertDisabled, setIsInsertDisabled] = useState(false);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState("#FFFFFF");
  const [textColor, setTextColor] = useState("#000000");
  const [autoScrollRows, setAutoScrollRows] = useState(2);
  const [followPartnerScroll, setFollowPartnerScroll] = useState(false);
  const [shouldScroll, setShouldScroll] = useState(true);

  const [showName, setShowName] = useState(true);
  const [nameWidth, setNameWidth] = useState(5);
  const [scriptWidth, setScriptWidth] = useState(45);
  const [transWidth, setTransWidth] = useState(45);

  const [sheetsArray, setSheetsArray] = useState([]);

  const [sendTranslationKey, setSendTranslationKey] = useState("F4");
  const [sendScriptKey, setSendScriptKey] = useState("Off");
  const [moveUpKey, setMoveUpKey] = useState("ArrowUp");
  const [moveDownKey, setMoveDownKey] = useState("ArrowDown");
  // const [clearSubtitlesKey, setClearSubtitlesKey] = useState("F8");

  const [showBookmarks, setShowBookmarks] = useState(false);

  const scrollParentRef = useRef(null);
  const theadRef = useRef(null);
  const tbodyRef = useRef(null);

  // tbody는 스크롤 컨테이너의 맨 위가 아니라 thead 아래에서 시작한다. 이 오프셋을
  // scrollMargin으로 알려주지 않으면 scrollToIndex가 thead 높이만큼 덜 스크롤한다.
  const [listOffset, setListOffset] = useState(0);
  // 계측용(#148). listOffset이 첫 페인트 이후 바뀌면 리스트 전체가 균일하게 밀리는데,
  // 이게 렌더 잔상의 트리거로 의심된다. 잔상 발생 시각과 아래 로그를 대조하면 확인된다.
  const listOffsetRef = useRef(0);
  const hasSheets = sheets.length > 0;

  useLayoutEffect(() => {
    const parent = scrollParentRef.current;
    const body = tbodyRef.current;
    const head = theadRef.current;
    if (!parent || !body) return;

    const measure = (reason) => {
      const offset =
        body.getBoundingClientRect().top -
        parent.getBoundingClientRect().top +
        parent.scrollTop;
      const prev = listOffsetRef.current;
      // 소수점 흔들림으로 무한 렌더 루프가 돌지 않도록 1px 이상 차이날 때만 반영
      if (Math.abs(prev - offset) <= 1) {
        return;
      }
      listOffsetRef.current = offset;
      // TODO(#148): 잔상 원인 확정되면 제거
      console.log(
        `[SheetsView] listOffset ${prev} -> ${offset} (${reason}) @ ${new Date().toISOString()}`
      );
      setListOffset(offset);
    };

    measure("mount");
    // thead 높이는 컬럼 폭 변경·창 리사이즈로 줄바꿈이 생길 때만 바뀐다
    const observer = new ResizeObserver(() => measure("resize"));
    if (head) observer.observe(head);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [hasSheets, showName]);

  const rowVirtualizer = useVirtualizer({
    count: sheets.length,
    getScrollElement: () => scrollParentRef.current,
    // 본쇼 사이즈 시트 3구간(앞/중/뒤) 실측 평균 54px. 42(짧은 행)~83 분포.
    // 낮게 잡으면 측정이 진행되며 전체 높이가 커져 스크롤바가 점프한다.
    estimateSize: () => 54,
    overscan: 10,
    scrollMargin: listOffset,
    getItemKey: (i) => sheets[i]?.id ?? i,
  });

  const executeScroll = () => {
    const target = Math.max(0, selectedRow - Number(autoScrollRows));
    rowVirtualizer.scrollToIndex(target, { align: "start" });
  };
  const executePartnerScroll = () => {
    const target = Math.max(
      0,
      partnerSelectedRow - Number(autoScrollRows)
    );
    rowVirtualizer.scrollToIndex(target, { align: "start" });
  };

  function handleSendSelectedTranslation() {
    // Don't create message if tranlsation is empty
    if (sheets[selectedRow].translation === "") {
      setSelectedRow((prev) => prev + 1);
      setShouldScroll(true);
      return;
    }
    // 전송은 MessageSender 큐에 넣기만 하고 행은 바로 이동시켜야 시트가 큰 세션에서도 즉시 반응한다.
    // 지연·실패 보고는 MessageSender가 한다
    const text = sheets[selectedRow].translation;
    const sentRow = selectedRow;
    setSelectedRow((prev) => prev + 1);
    setShouldScroll(true);
    sendMessage({
      sessionId: props.sessionId,
      text,
      scriptIndex: sentRow + 1,
      context: "SheetsView.handleSendSelectedTranslation",
    });
  }

  function handleSendSelectedScript() {
    // Don't create message if script is empty
    if (sheets[selectedRow].script === "") {
      setSelectedRow((prev) => prev + 1);
      setShouldScroll(true);
      return;
    }
    const text = sheets[selectedRow].script;
    const sentRow = selectedRow;
    setSelectedRow((prev) => prev + 1);
    setShouldScroll(true);
    sendMessage({
      sessionId: props.sessionId,
      text,
      scriptIndex: sentRow + 1,
      context: "SheetsView.handleSendSelectedScript",
    });
  }

  // for sending translation to YouTube
  // isYoutubeView boolean variable to prevent rerender when in regular translator view
  const handleSheetsInput = useCallback((text) => {
    if (props.isYoutubeView) {
      props.setSheetsInput(text);
      props.setSheetsInputIndex(
        (prevSheetsInputIndex) => prevSheetsInputIndex + 1
      );
    }
  }, [props.isYoutubeView, props.setSheetsInput, props.setSheetsInputIndex]);

  // Sheets initial fetch
  useEffect(() => {
    async function initialFetch() {
      const sheets = await fetchScriptSheets(props.sessionId);
      setSheets(sheets);
    }
    initialFetch();
  }, [props.sessionId]);

  // User Key Command initial fetch
  useEffect(() => {
    async function initialFetch() {
      const fetchedKeyCommands = await fetchUserKeyCommands(userContext.userId);
      const hasValue = (command) => {
        for (const obj of fetchedKeyCommands) {
          if (obj.commandName === command) {
            return true;
          }
        }
        return false;
      };

      hasValue("sendTranslation") &&
        setSendTranslationKey(
          fetchedKeyCommands.find(
            (command) => command.commandName === "sendTranslation"
          ).triggerKey
        );
      hasValue("sendScript") &&
        setSendScriptKey(
          fetchedKeyCommands.find(
            (command) => command.commandName === "sendScript"
          ).triggerKey
        );
      hasValue("moveUp") &&
        setMoveUpKey(
          fetchedKeyCommands.find((command) => command.commandName === "moveUp")
            .triggerKey
        );
      hasValue("moveDown") &&
        setMoveDownKey(
          fetchedKeyCommands.find(
            (command) => command.commandName === "moveDown"
          ).triggerKey
        );
    }
    initialFetch();
  }, [props.sessionId]);

  // Subscribe to session sheets update count - with reconnect
  useEffect(() => {
    let isMounted = true;
    let subscription;

    const subscribe = () => {
      subscription?.unsubscribe?.();

      subscription = API.graphql({
        query: onUpdateSession,
        variables: { id: props.sessionId },
      }).subscribe({
        next: () => {
          async function updateFetch() {
            const sheets = await fetchScriptSheets(props.sessionId);
            if (sheets !== undefined) {
              setSheets(sheets);
            }
          }
          updateFetch();
        },
        error: (error) => {
          console.warn(error);
          const message = error?.error?.errors?.[0]?.message ?? error?.errors?.[0]?.message;
          Sentry.captureException(
            new Error(message ?? "onUpdateSession subscription error"),
            { extra: { context: "onUpdateSession subscription", sessionId: props.sessionId, rawError: error } }
          );
          if (!isMounted) return;
          if (message === "Connection closed") {
            setTimeout(() => {
              if (isMounted) {
                console.log("[SheetsView] Re-subscribing onUpdateSession...");
                subscribe();
              }
            }, 1000);
          }
        },
      });
    };

    subscribe();

    return () => {
      isMounted = false;
      subscription?.unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleHotkeyPress);
    return () => {
      window.removeEventListener("keydown", handleHotkeyPress);
    };
  }, [sendTranslationKey, sendScriptKey, moveDownKey, moveUpKey]);

  // Prevent selectedRow from exceeding the sheet length
  useEffect(() => {
    if (selectedRow > sheets.length - 1) {
      setSelectedRow(sheets.length - 1);
      // console.log('reached end');
    }
  }, [selectedRow]);

  // Publish selectedRow to PubSub
  useEffect(() => {
    publishSelectedRow();
  }, [selectedRow]);

  // Subscribe to partner's selectedRow
  useEffect(() => {
    if (props.selectedUser === "") {
      subscription && subscription.unsubscribe();
      setPartnerSelectedRow(-1);
      return;
    }
    subscription && subscription.unsubscribe();
    const topic = getSessionUsernameTopic(
      `${props.sessionId}sheets`,
      props.selectedUser
    );
    const sub = PubSub.subscribe(topic).subscribe({
      next: (data) => {
        setPartnerSelectedRow(data.value);
      },
      error: (error) => console.error(error),
      complete: () => console.log("Done"),
    });
    setSubscription(sub);
    return () => {
      subscription && subscription.unsubscribe();
    };
  }, [props.username, props.sessionId, props.selectedUser]);

  // Auto-scroll to selection.
  // selectedRow를 바꾸는 쪽에서 executeScroll()을 직접 부르면 setState 이전 값이
  // 잡혀 한 행씩 어긋난다. 스크롤 트리거는 이 effect 하나로 통일한다.
  useEffect(() => {
    if (selectedRow > -1 && shouldScroll) {
      executeScroll();
    }
  }, [selectedRow, shouldScroll]);

  // Auto-scroll to partner selection
  useEffect(() => {
    if (partnerSelectedRow > -1 && followPartnerScroll) {
      executePartnerScroll();
    }
  }, [partnerSelectedRow]);

  async function fetchScriptSheets(sessionId) {
    try {
      const apiData = await API.graphql({
        query: queryScriptSheetsBySessionSheetsIdIndex,
        variables: {
          sessionSheetsId: sessionId,
        },
      });
      return apiData.data.queryScriptSheetsBySessionSheetsIdIndex.items;
    } catch (e) {
      console.log(e);
      Sentry.captureException(e, { extra: { context: "fetchScriptSheets", sessionId } });
    }
  }

  async function createScriptSheets(
    sessionId: string,
    index: number,
    script: string,
    translation: string,
    backgroundColor: string,
    textColor: string
  ) {
    const ret = await API.graphql({
      query: createScriptSheetsMutation,
      variables: {
        input: {
          sessionSheetsId: sessionId,
          isSelected: false,
          isEditable: false,
          index: index,
          script: script,
          translation: translation,
          backgroundColor: backgroundColor,
          textColor: textColor,
        },
      },
    });
  }

  async function updateScriptSheetsIndex(id: string, index: number) {
    const ret = await API.graphql({
      query: updateScriptSheetsMutation,
      variables: {
        input: {
          id: id,
          index: index,
        },
      },
    });
  }

  async function publishSelectedRow() {
    if (userContext.username === "") {
      return;
    }
    const topic = getSessionUsernameTopic(
      `${props.sessionId}sheets`,
      userContext.username
    );
    await PubSub.publish(topic, selectedRow);
  }

  async function handleInsertRow() {
    if (isInsertDisabled) return; // Prevent double-clicking

    // Set necessary indexes as variables
    const selectedIndex = selectedRow;
    const currentSparseIndex = sheets[selectedIndex].index;
    const nextSparseIndex = sheets[selectedIndex + 1].index;

    // Get necessary format value
    const currentBackgroundColor = sheets[selectedIndex].backgroundColor;
    const currentTextColor = sheets[selectedIndex].textColor;

    // Calculate new index to insert between current sparse idx and next sparse idx
    const newIndex = (currentSparseIndex + nextSparseIndex) / 2;

    // Check if there's a space for the new index
    if (newIndex === currentSparseIndex || newIndex === nextSparseIndex) {
      alert(
        `Unable to insert a new row at current position : New index(${newIndex}) is identical to current row index(${currentSparseIndex}) or next row index(${nextSparseIndex})`
      );
      return;
    }

    setIsInsertDisabled(true);
    try {
      // Insert a new row in a newly calculated position
      await createScriptSheets(
        props.sessionId,
        newIndex,
        "",
        "",
        currentBackgroundColor,
        currentTextColor
      );

      // update sheetsUpdateCount attribute
      await updateSessionSheetsUpdateCount(props.sessionId);
    } catch (err) {
      console.error(err);
      alert("행 삽입 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsInsertDisabled(false);
    }
  }

  function handleUpdateBackgroundColor() {
    updateScriptSheetsBackgroundColor(sheets[selectedRow].id, backgroundColor);
    setTimeout(() => {
      updateSessionSheetsUpdateCount(props.sessionId);
    }, 500);
  }
  function handleUpdateTextColor() {
    updateScriptSheetsTextColor(sheets[selectedRow].id, textColor);
    setTimeout(() => {
      updateSessionSheetsUpdateCount(props.sessionId);
    }, 500);
  }

  async function handleDeleteSelectedRow() {
    if (isDeleteDisabled) return; // Prevent double-clicking
    const index = selectedRow;

    setIsDeleteDisabled(true);
    try {
      await deleteScriptSheets(sheets[index].id);
      await updateSessionSheetsUpdateCount(props.sessionId);
    } catch (err) {
      console.error(err);
      alert("행 삭제 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsDeleteDisabled(false);
    }
  }

  const handleHotkeyPress = (e) => {
    if (e.key === sendTranslationKey) {
      // Cancel the default action, if needed
      e.preventDefault();
      document.getElementById("send-translation").click();
    } else if (e.key === sendScriptKey) {
      // Cancel the default action, if needed
      e.preventDefault();
      document.getElementById("send-script").click();
    } else if (e.key === moveUpKey) {
      e.preventDefault();
      setSelectedRow((prev) => {
        if (prev > 0) {
          return prev - 1;
        } else {
          return prev;
        }
      });
      setShouldScroll(true);
    } else if (e.key === moveDownKey) {
      e.preventDefault();
      setSelectedRow((prev) => prev + 1);
      setShouldScroll(true);
    }
  };

  return (
    <Container className="m-0 p-0">
      <Card>
        <Card.Header as="h5">
          <Row>
            <Col xs={2}>Script</Col>
            <Col xs={10}>
              <Button
                title="Send selected row and move down"
                id="send-translation"
                className="float-end ms-1"
                variant="success"
                size="sm"
                disabled={props.isYoutubeView && props.prevDisplay}
                onClick={() => {
                  handleSendSelectedTranslation();
                }}
              >
                Send Translation
                {sendTranslationKey === "Off"
                  ? null
                  : ` (${sendTranslationKey})`}
                {/* ({selectedRow + 1}) */}
              </Button>
              {sendScriptKey === "Off" ? null : (
                <Button
                  title="Send selected script and move down"
                  id="send-script"
                  className="float-end ms-1"
                  variant="success"
                  size="sm"
                  disabled={props.isYoutubeView && props.prevDisplay}
                  onClick={() => {
                    handleSendSelectedScript();
                  }}
                >
                  Send Script ({sendScriptKey}){/* ({selectedRow + 1}) */}
                </Button>
              )}
              <Button
                className="float-end ms-1"
                variant="outline-secondary"
                size="sm"
                // disabled
                onClick={() => {
                  setShowEditModal(true);
                }}
              >
                Google Sheets
              </Button>
              <Button
                className="float-end ms-1"
                variant={showMenu ? "secondary" : "outline-secondary"}
                size="sm"
                onClick={() => {
                  setShowMenu((prev) => !prev);
                }}
              >
                Menu
              </Button>
              <Button
                className="float-end"
                variant={showBookmarks ? "secondary" : "outline-secondary"}
                size="sm"
                onClick={() => {
                  setShowBookmarks((prev) => !prev);
                }}
              >
                Bookmarks
              </Button>
            </Col>
          </Row>
        </Card.Header>
        {showMenu ? (
          <Container fluid className="m-0 p-0">
            <Card>
              <Card.Body className="m-1 p-0">
                <Row className="m-1">
                  <Col xs={4}>
                    <Row>
                      <Col>
                        <Button
                          className="float-start"
                          size="sm"
                          variant="outline-secondary"
                          onClick={handleUpdateBackgroundColor}
                        >
                          Apply Row Color
                        </Button>
                        <Form.Control
                          className="float-start"
                          size="sm"
                          type="color"
                          value={backgroundColor}
                          onChange={(event) => {
                            setBackgroundColor(event.target.value);
                          }}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Button
                          className="float-start"
                          size="sm"
                          variant="outline-secondary"
                          onClick={handleUpdateTextColor}
                        >
                          Apply Text Color
                        </Button>
                        <Form.Control
                          className="float-start"
                          size="sm"
                          type="color"
                          value={textColor}
                          onChange={(event) => {
                            setTextColor(event.target.value);
                          }}
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={5}>
                    <Stack direction="horizontal" gap={2}>
                      <div>
                        <Form.Label>Auto scroll rows:</Form.Label>
                        <Form.Control
                          style={{ width: "60%" }}
                          size="sm"
                          min="0"
                          type="number"
                          value={autoScrollRows}
                          onChange={(event) => {
                            setAutoScrollRows(event.target.value);
                          }}
                        />
                      </div>
                      <div>
                        <Form>
                          <Form.Check
                            label="Follow Partner"
                            checked={followPartnerScroll}
                            onChange={() => {
                              setFollowPartnerScroll((prev) => !prev);
                            }}
                          />
                        </Form>

                        <Form>
                          <Form.Check
                            label="Show Name"
                            checked={showName}
                            onChange={() => {
                              setShowName((prev) => !prev);
                            }}
                          />
                        </Form>
                      </div>
                    </Stack>
                  </Col>
                  <Col xs={3}>
                    <Button
                      className="float-end"
                      variant="outline-danger"
                      size="sm"
                      onClick={(event) => {
                        if (event.detail >= 2) {
                          event.stopPropagation();
                        } else {
                          handleDeleteSelectedRow();
                        }
                      }}
                      disabled={isDeleteDisabled || isInsertDisabled}
                    >
                      {isDeleteDisabled ? "Deleting..." : "Delete Row"}
                    </Button>
                    <Button
                      className="float-end"
                      variant="outline-success"
                      size="sm"
                      onClick={(event) => {
                        if (event.detail >= 2) {
                          event.stopPropagation();
                        } else {
                          handleInsertRow();
                        }
                      }}
                      disabled={isInsertDisabled || isDeleteDisabled}
                    >
                      {isInsertDisabled ? "Inserting..." : "Insert Row"}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            <Card>
              <Card.Body className="m-1 p-0">
                <Row className="d-flex ms-3" style={{ width: "50%" }}>
                  <Col
                    style={{ fontWeight: "bold" }}
                    className="m-0 p-0"
                    xs={3}
                  >
                    Column Widths (%)
                  </Col>
                  <Col xs={3}>
                    <Form.Label className="m-0 p-0">Name</Form.Label>
                    <Form.Control
                      style={{ width: "100%" }}
                      size="sm"
                      min="1"
                      type="number"
                      value={nameWidth}
                      onChange={(event) => {
                        setNameWidth(event.target.value);
                      }}
                    />
                  </Col>
                  <Col xs={3}>
                    <Form.Label className="m-0 p-0">Script</Form.Label>
                    <Form.Control
                      style={{ width: "100%" }}
                      size="sm"
                      min="1"
                      type="number"
                      value={scriptWidth}
                      onChange={(event) => {
                        setScriptWidth(event.target.value);
                      }}
                    />
                  </Col>
                  <Col xs={3}>
                    <Form.Label className="m-0 p-0">Trans</Form.Label>
                    <Form.Control
                      style={{ width: "100%" }}
                      size="sm"
                      min="1"
                      type="number"
                      value={transWidth}
                      onChange={(event) => {
                        setTransWidth(event.target.value);
                      }}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Container>
        ) : null}
        {showBookmarks ? (
          <BookmarksBar
            sessionId={props.sessionId}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            executeScroll={executeScroll}
            setShouldScroll={setShouldScroll}
          />
        ) : null}
        <Card.Body className="m-0 p-0">
          <Container
            ref={scrollParentRef}
            className="p-0 m-0"
            style={{
              maxHeight: "77vh",
              // maxHeight: "560px",
              overflow: "scroll",
              overflowX: "hidden",
              // 가상화 spacer 높이가 바뀔 때 이전 프레임이 지워지지 않고 남는 잔상(#148)
              // 대응. 페인트를 이 박스 안으로 격리해 무효화 영역이 정확히 잡히게 한다.
              contain: "paint",
            }}
          >
            <Row>
              <Col>
                <Table
                  bordered
                  // hover
                >
                  <thead ref={theadRef}>
                    <tr align="center">
                      {/* <th style={{ width: "10px" }}>#</th> */}
                      <th style={{ width: "4%" }}>#</th>
                      {showName ? (
                        <th style={{ width: `${nameWidth}%` }}>Name</th>
                      ) : null}
                      <th style={{ width: `${scriptWidth}%` }}>Script</th>
                      <th style={{ width: `${transWidth}%` }}>Translation</th>
                      {/* <th>Count</th> */}
                      <th style={{ width: "4%" }}>Send</th>
                    </tr>
                  </thead>
                  <SheetRows
                    sheets={sheets}
                    setSheets={setSheets}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                    editableScript={editableScript}
                    // setEditableRow={setEditableRow}
                    editableTranslation={editableTranslation}
                    setEditableScript={setEditableScript}
                    setEditableTranslation={setEditableTranslation}
                    isYoutubeView={props.isYoutubeView}
                    prevDisplay={props.prevDisplay}
                    sessionId={props.sessionId}
                    setSheetsInput={props.setSheetsInput}
                    setSheetsInputIndex={props.setSheetsInputIndex}
                    handleSheetsInput={handleSheetsInput}
                    partnerSelectedRow={partnerSelectedRow}
                    partnerName={props.selectedUser}
                    showName={showName}
                    setShouldScroll={setShouldScroll}
                    rowVirtualizer={rowVirtualizer}
                    tbodyRef={tbodyRef}
                  />
                </Table>
              </Col>
            </Row>
          </Container>
        </Card.Body>
      </Card>
      <SheetsLoadView
        sessionId={props.sessionId}
        sessionName={props.sessionName}
        show={showEditModal}
        sheets={sheets}
        setSheets={setSheets}
        onHide={() => {
          setShowEditModal(false);
        }}
      />
    </Container>
  );
}
